import React from 'react';
import { 
  FileText, 
  Clock, 
  Target, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const STEPS = [
  {
    number: '01',
    title: 'Paste your feedback',
    description: 'Drop in any performance feedback, project notes, or accomplishments from the quarter.',
    icon: FileText,
    color: 'red'
  },
  {
    number: '02',
    title: 'We find the evidence',
    description: 'Our AI identifies achievements, skills demonstrated, and impact statements from your input.',
    icon: Target,
    color: 'amber'
  },
  {
    number: '03',
    title: 'Get a polished review',
    description: 'Receive a structured self-review with accomplishments, growth areas, and goals — ready to submit.',
    icon: CheckCircle,
    color: 'green'
  }
];

const HowItWorks = ({ series }) => {
  const accentColor = series?.accentColor || '#dc2626';

  return (
    <section className="py-20 bg-white" data-testid="review-how-it-works">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-sm text-slate-600 mb-6">
              <Clock className="w-4 h-4" />
              <span>From blank page to done in 10 minutes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No more staring at an empty document. We turn your scattered notes into a compelling narrative.
            </p>
          </div>

          {/* Steps */}
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2 hidden lg:block" />

            <div className="grid lg:grid-cols-3 gap-8">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isLast = index === STEPS.length - 1;
                
                return (
                  <div 
                    key={step.number}
                    className="relative bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`review-step-${index + 1}`}
                  >
                    {/* Step number badge */}
                    <div 
                      className="absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      Step {step.number}
                    </div>

                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 mt-2"
                      style={{ backgroundColor: `${accentColor}10` }}
                    >
                      <Icon className="w-7 h-7" style={{ color: accentColor }} />
                    </div>

                    {/* Content */}
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {step.description}
                    </p>

                    {/* Arrow to next (hidden on mobile and last item) */}
                    {!isLast && (
                      <div className="absolute -right-4 top-1/2 -translate-y-1/2 hidden lg:block z-10">
                        <div className="w-8 h-8 bg-white rounded-full border border-slate-200 flex items-center justify-center shadow-sm">
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
